import { LitElement, html, css, unsafeCSS } from "lit";
import { customElement, state, query } from "lit/decorators.js";
import L from "leaflet";
import leafletCss from "leaflet/dist/leaflet.css?inline";

import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/outlined-button.js";
import "@material/web/button/text-button.js";
import "@material/web/icon/icon.js";
import "@material/web/progress/circular-progress.js";

import "../components/app-shell";
import { sharedStyles } from "../styles/shared";
import { icon } from "../lib/icons";
import { inputValue } from "../lib/dom";
import { navigate } from "../lib/navigation";
import { toast } from "../lib/toast";
import { companies, type Company, type DupPair } from "../lib/companies";
import { ApiError } from "../lib/api";

const STATUSES = ["new", "contacted", "interested", "customer", "declined"];

@customElement("companies-page")
export class CompaniesPage extends LitElement {
  static styles = [
    sharedStyles,
    unsafeCSS(leafletCss),
    css`
      .page {
        padding: var(--space-4, 16px);
        max-width: 1200px;
        margin: 0 auto;
      }
      @media (min-width: 600px) {
        .page {
          padding: var(--space-7, 28px) var(--space-8, 32px);
        }
      }
      .head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 18px;
      }
      h1 {
        margin: 0;
        font-size: 26px;
      }
      .sub {
        color: var(--md-sys-color-on-surface-variant);
        font-size: 14px;
      }
      .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        align-items: center;
        margin-bottom: 16px;
      }
      .filters md-outlined-text-field {
        flex: 1;
        min-width: 220px;
      }
      #map {
        height: 300px;
        border-radius: 14px;
        overflow: hidden;
        border: 1px solid var(--md-sys-color-outline-variant);
        margin-bottom: 18px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
      }
      th {
        text-align: left;
        padding: 10px 12px;
        color: var(--md-sys-color-on-surface-variant);
        font-weight: 600;
        border-bottom: 1px solid var(--md-sys-color-outline-variant);
      }
      td {
        padding: 12px;
        border-bottom: 1px solid var(--md-sys-color-outline-variant);
      }
      tr.row {
        cursor: pointer;
      }
      tr.row:hover td {
        background: color-mix(in srgb, var(--md-sys-color-on-surface) 5%, transparent);
      }
      .chip {
        display: inline-block;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
        text-transform: capitalize;
      }
      .muted {
        color: var(--md-sys-color-on-surface-variant);
      }
      .pager {
        display: flex;
        gap: 10px;
        align-items: center;
        justify-content: flex-end;
        margin-top: 14px;
      }
      .center {
        padding: 60px;
        text-align: center;
      }
      .add-row {
        display: flex;
        gap: 10px;
        align-items: center;
        margin-bottom: 16px;
      }
      .import-panel {
        background: color-mix(in srgb, var(--md-sys-color-primary) 5%, transparent);
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 14px;
        padding: 18px;
        margin-bottom: 16px;
      }
      .ip-title {
        font-weight: 600;
        margin-bottom: 12px;
      }
      .ip-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 10px;
      }
      @media (min-width: 600px) {
        .ip-grid {
          grid-template-columns: 1fr 1fr;
        }
      }
      @media (min-width: 840px) {
        .ip-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }
      .ip-actions {
        display: flex;
        gap: 12px;
        align-items: center;
        margin-top: 12px;
      }
      .actions-row {
        display: flex;
        gap: 8px;
      }
      .dup {
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 12px;
        padding: 12px;
        margin-bottom: 10px;
      }
      .dup-cols {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
      }
      @media (min-width: 600px) {
        .dup-cols {
          grid-template-columns: 1fr 1fr;
        }
      }
      .dup-card {
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 10px;
        padding: 10px;
      }
      .dup-card .who {
        font-weight: 600;
      }
      .dup-reason {
        margin-top: 8px;
        font-style: italic;
      }
      .error {
        color: var(--md-sys-color-error);
      }
    `,
  ];

  @state() private rows: Company[] = [];
  @state() private loading = true;
  @state() private error = "";
  @state() private q = "";
  @state() private status = "";
  @state() private page = 1;
  @state() private adding = false;
  @state() private newName = "";
  @state() private importOpen = false;
  @state() private importing = false;
  @state() private reconcileOpen = false;
  @state() private reconciling = false;
  @state() private pairs: DupPair[] = [];
  @state() private imp = {
    source: "gouv-fr",
    naf: "11.02A",
    departements: "51,10,02",
    industry: "wine",
    region: "Champagne",
    maxPages: 2,
  };

  @query("#map") private mapEl!: HTMLDivElement;
  private map?: L.Map;
  private markers?: L.LayerGroup;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      const res = await companies.list({
        q: this.q,
        status: this.status,
        page: this.page,
        perPage: 50,
      });
      this.rows = res.companies;
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Failed to load companies";
    } finally {
      this.loading = false;
    }
  }

  protected updated(): void {
    this.renderMap();
  }

  private renderMap(): void {
    if (!this.mapEl) return;
    if (!this.map) {
      this.map = L.map(this.mapEl).setView([48.6, 4.2], 6);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap",
        maxZoom: 19,
      }).addTo(this.map);
      this.markers = L.layerGroup().addTo(this.map);
      setTimeout(() => this.map?.invalidateSize(), 0);
    }
    this.markers?.clearLayers();
    const pts: L.LatLngExpression[] = [];
    for (const c of this.rows) {
      if (c.lat && c.lng) {
        L.circleMarker([c.lat, c.lng], {
          radius: 7,
          color: "#2e6b54",
          fillColor: "#2e6b54",
          fillOpacity: 0.75,
          weight: 2,
        })
          .bindPopup(`<b>${c.name}</b><br>${c.city || ""} ${c.region || ""}`)
          .on("click", () => navigate(`/companies/${c.id}`))
          .addTo(this.markers!);
        pts.push([c.lat, c.lng]);
      }
    }
    if (pts.length) this.map.fitBounds(L.latLngBounds(pts).pad(0.25));
  }

  private search(): void {
    this.page = 1;
    void this.load();
  }

  private async addCompany(): Promise<void> {
    if (!this.newName.trim()) return;
    try {
      const c = await companies.create({ name: this.newName.trim(), status: "new" });
      this.adding = false;
      this.newName = "";
      navigate(`/companies/${c.id}`);
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Could not create company";
    }
  }

  private async runImport(): Promise<void> {
    this.importing = true;
    this.error = "";
    try {
      const { industry, region, maxPages } = this.imp;
      const r =
        this.imp.source === "comite-champagne"
          ? await companies.importComite({ industry, region, maxPages })
          : await companies.importGouvFr(this.imp);
      toast(`Imported: ${r.created} new, ${r.updated} updated (${r.fetched} fetched)`);
      this.importOpen = false;
      this.page = 1;
      await this.load();
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Import failed";
    } finally {
      this.importing = false;
    }
  }

  private async openReconcile(): Promise<void> {
    this.reconcileOpen = !this.reconcileOpen;
    if (this.reconcileOpen) await this.loadPairs();
  }

  private async loadPairs(): Promise<void> {
    this.reconciling = true;
    this.error = "";
    try {
      this.pairs = await companies.duplicates();
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Could not scan for duplicates";
    } finally {
      this.reconciling = false;
    }
  }

  private async merge(keepId: string, dropId: string): Promise<void> {
    try {
      await companies.merge(keepId, dropId);
      toast("Merged");
      this.pairs = this.pairs.filter((p) => p.a.id !== dropId && p.b.id !== dropId && p.a.id !== keepId && p.b.id !== keepId);
      await this.load();
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Merge failed";
    }
  }

  private renderPair(p: DupPair) {
    return html`<div class="dup">
      <div class="dup-cols">
        ${[p.a, p.b].map(
          (c, i) => html`<div class="dup-card">
            <div class="who">${c.name}</div>
            <div class="muted">${c.registryId ? html`SIREN ${c.registryId} · ` : ""}${c.city || ""} ${c.postalCode || ""}</div>
            <div class="muted">${c.source || ""}${c.website ? html` · ${c.website}` : ""}</div>
            <md-text-button @click=${() => void this.merge(c.id, i === 0 ? p.b.id : p.a.id)}>Keep this ←</md-text-button>
          </div>`,
        )}
      </div>
      <div class="muted dup-reason">${p.reason}</div>
    </div>`;
  }

  render() {
    return html`
      <app-shell active="/companies">
        <div class="page">
          <div class="head">
            <div>
              <h1>Producers</h1>
              <div class="sub">Company registry — find, contact, onboard.</div>
            </div>
            <div class="actions-row">
              <md-outlined-button @click=${() => void this.openReconcile()}>
                ${icon("hub")} Reconcile
              </md-outlined-button>
              <md-outlined-button @click=${() => (this.importOpen = !this.importOpen)}>
                ${icon("download")} Import
              </md-outlined-button>
              <md-filled-button @click=${() => (this.adding = !this.adding)}>
                ${icon("add")} Add company
              </md-filled-button>
            </div>
          </div>

          ${this.importOpen
            ? html`<div class="import-panel">
                <div class="ip-title">Import producers from a source</div>
                <div class="ip-grid">
                  <md-outlined-select
                    label="Source"
                    .value=${this.imp.source}
                    @change=${(e: Event) =>
                      (this.imp = { ...this.imp, source: (e.target as HTMLSelectElement).value })}
                  >
                    <md-select-option value="gouv-fr">
                      <div slot="headline">gouv.fr business registry</div>
                    </md-select-option>
                    <md-select-option value="comite-champagne">
                      <div slot="headline">Comité-Champagne directory</div>
                    </md-select-option>
                  </md-outlined-select>
                  ${this.imp.source === "gouv-fr"
                    ? html`
                        <md-outlined-text-field
                          label="NAF / activity code"
                          .value=${this.imp.naf}
                          @input=${(e: Event) => (this.imp = { ...this.imp, naf: inputValue(e) })}
                        ></md-outlined-text-field>
                        <md-outlined-text-field
                          label="Departments (comma)"
                          .value=${this.imp.departements}
                          @input=${(e: Event) => (this.imp = { ...this.imp, departements: inputValue(e) })}
                        ></md-outlined-text-field>
                      `
                    : null}
                  <md-outlined-text-field
                    label="Industry label"
                    .value=${this.imp.industry}
                    @input=${(e: Event) => (this.imp = { ...this.imp, industry: inputValue(e) })}
                  ></md-outlined-text-field>
                  <md-outlined-text-field
                    label="Region label"
                    .value=${this.imp.region}
                    @input=${(e: Event) => (this.imp = { ...this.imp, region: inputValue(e) })}
                  ></md-outlined-text-field>
                  <md-outlined-text-field
                    label=${this.imp.source === "gouv-fr" ? "Max pages (×25)" : "Max pages (×12)"}
                    type="number"
                    .value=${String(this.imp.maxPages)}
                    @input=${(e: Event) => (this.imp = { ...this.imp, maxPages: Number(inputValue(e)) })}
                  ></md-outlined-text-field>
                </div>
                <div class="ip-actions">
                  <md-filled-button ?disabled=${this.importing} @click=${() => void this.runImport()}>
                    ${this.importing ? "Importing…" : "Import"}
                  </md-filled-button>
                  <span class="muted">
                    ${this.imp.source === "gouv-fr"
                      ? "NAF 11.02A = sparkling wine; depts 51 / 10 / 02 = Champagne."
                      : "Official champagne.fr directory — rich contact data (website, email, phone), no legal id."}
                  </span>
                </div>
              </div>`
            : null}

          ${this.reconcileOpen
            ? html`<div class="import-panel">
                <div class="ip-title">Reconcile duplicates</div>
                ${this.reconciling
                  ? html`<div class="center"><md-circular-progress indeterminate></md-circular-progress></div>`
                  : this.pairs.length === 0
                    ? html`<p class="muted">No likely duplicates found.</p>`
                    : html`<div class="muted" style="margin-bottom:12px">
                          ${this.pairs.length} candidate pair(s). Keep one record — the other folds into it
                          (fills gaps, moves contacts, unions the dedupe keys).
                        </div>
                        ${this.pairs.map((p) => this.renderPair(p))}`}
              </div>`
            : null}

          ${this.adding
            ? html`<div class="add-row">
                <md-outlined-text-field
                  label="Company name"
                  .value=${this.newName}
                  @input=${(e: Event) => (this.newName = inputValue(e))}
                  @keydown=${(e: KeyboardEvent) => e.key === "Enter" && this.addCompany()}
                ></md-outlined-text-field>
                <md-filled-button @click=${() => void this.addCompany()}>Create</md-filled-button>
                <md-text-button @click=${() => (this.adding = false)}>Cancel</md-text-button>
              </div>`
            : null}

          <div class="filters">
            <md-outlined-text-field
              label="Search name, registry id, city…"
              type="search"
              .value=${this.q}
              @input=${(e: Event) => (this.q = inputValue(e))}
              @keydown=${(e: KeyboardEvent) => e.key === "Enter" && this.search()}
            ></md-outlined-text-field>
            <md-outlined-select
              label="Status"
              .value=${this.status}
              @change=${(e: Event) => {
                this.status = (e.target as HTMLSelectElement).value;
                this.search();
              }}
            >
              <md-select-option value=""><div slot="headline">All statuses</div></md-select-option>
              ${STATUSES.map(
                (s) => html`<md-select-option value=${s}><div slot="headline">${s}</div></md-select-option>`,
              )}
            </md-outlined-select>
            <md-outlined-button @click=${() => this.search()}>${icon("search")} Search</md-outlined-button>
          </div>

          <div id="map"></div>

          ${this.error ? html`<p class="error">${this.error}</p>` : null}
          ${this.loading
            ? html`<div class="center"><md-circular-progress indeterminate></md-circular-progress></div>`
            : this.rows.length === 0
              ? html`<div class="center muted">No producers yet. Add one, or import from a source.</div>`
              : html`
                  <div class="table-scroll"><table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Industry</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${this.rows.map(
                        (c) => html`
                          <tr class="row" @click=${() => navigate(`/companies/${c.id}`)}>
                            <td>
                              <strong>${c.name}</strong>
                              ${c.registry_id
                                ? html`<div class="muted">${c.registry_id}</div>`
                                : null}
                            </td>
                            <td>
                              ${c.industry || html`<span class="muted">—</span>`}
                              ${c.sub_type ? html`<div class="muted">${c.sub_type}</div>` : null}
                            </td>
                            <td>
                              ${c.city || ""} ${c.region ? html`<span class="muted">· ${c.region}</span>` : ""}
                              ${c.country ? html`<div class="muted">${c.country}</div>` : null}
                            </td>
                            <td><span class="chip">${c.status || "new"}</span></td>
                            <td>
                              ${c.email ? html`<span title=${c.email}>✉</span>` : ""}
                              ${c.phone ? html`<span title=${c.phone}>☎</span>` : ""}
                              ${c.website
                                ? html`<a
                                    href=${c.website}
                                    target="_blank"
                                    rel="noopener"
                                    title=${c.website}
                                    @click=${(e: Event) => e.stopPropagation()}
                                    >🌐</a
                                  >`
                                : ""}
                            </td>
                          </tr>
                        `,
                      )}
                    </tbody>
                  </table></div>
                  <div class="pager">
                    <md-text-button
                      ?disabled=${this.page <= 1}
                      @click=${() => {
                        this.page = Math.max(1, this.page - 1);
                        void this.load();
                      }}
                      >Prev</md-text-button
                    >
                    <span class="muted">Page ${this.page}</span>
                    <md-text-button
                      ?disabled=${this.rows.length < 50}
                      @click=${() => {
                        this.page += 1;
                        void this.load();
                      }}
                      >Next</md-text-button
                    >
                  </div>
                `}
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "companies-page": CompaniesPage;
  }
}
